import { SyncNotification } from '@/types/admin-sync-notification';
import { Tag } from 'antd';
import { AppTable, AppTooltip } from '../atoms';
import type { AppTableColumn } from '../atoms/AppTable';
import AppButton from '../atoms/AppButton';
import { Eye, Refresh } from '../atoms/AppIcon';
import { convGtJDateTime } from '@/utils/dateConverter';

interface SyncNotificationTableProps {
  data: SyncNotification[];
  /** Per-row 再同期 — re-push only this record (aggregate + aggregate_id). */
  onRetry: (aggregate: string, aggregateId: string) => void;
  /** 既読にする — mark this notification read. */
  onMarkRead: (id: number) => void;
  /** 明細CSV — download the rezepts behind one 状態の不一致 notice. Absent = the button is never drawn. */
  onDownloadCsv?: (id: number) => void;
  /** Notification id whose CSV is being fetched (per-row loading spinner). */
  downloadingId?: number | null;
  /** Disable all row actions while any mutation is in flight. */
  disabled?: boolean;
  /** aggregate_id currently being retried (per-row loading spinner). */
  retryingAggregateId?: string | null;
  /** Notification id currently being marked read (per-row loading spinner). */
  readingId?: number | null;
  isLoading?: boolean;
  /** Any 検索条件 in effect — decides which of AppTable's two empty texts is honest. */
  hasFilters?: boolean;
  /** BD 10.0 — show サイト区分. The server decides; see SyncNotificationSiteScope. */
  showSiteColumn?: boolean;
}

type SyncState = 'pending' | 'recovered';

/**
 * BD 13.0 — the sync outcome, derived from `status` and nothing else. Same rule as the backend's
 * SYNC_STATE_* so the column and the filter can never disagree.
 *
 * `attempts` IS NOT READ HERE ANY MORE. It used to make a third state, 同期失敗, out of 「attempts >= 2」 —
 * which is only about send attempts on the 同期失敗 inbox. On a 状態の不一致 row attempts counts DAILY
 * RECONCILE RUNS, so a month that stayed adrift for a week showed 同期失敗: a send failure that never
 * happened. How many tries it took is now read from 試行回数, where it means what it says.
 */
const syncStateOf = (r: SyncNotification): SyncState =>
  r.status === 'resolved' ? 'recovered' : 'pending';

const SYNC_STATE_LABELS: Record<SyncState, string> = {
  pending: '未同期',
  recovered: '同期成功',
};

const SYNC_STATE_COLORS: Record<SyncState, string> = {
  pending: 'orange',
  recovered: 'green',
};

/**
 * The 治療院コード 審査 sent, out of a rezept-status aggregate_id
 * (「{HOSP_CODE}-{P_CONSULT_NO}-{SUB_YEAR_MONTH}」), or null when the id is not that shape.
 */
const hospCodeOf = (record: SyncNotification): string | null => {
  const head = record.aggregate_id?.split('-')[0] ?? '';
  return /^\d+$/.test(head) && record.aggregate_id.includes('-') ? head : null;
};

/**
 * BD 8.0 / 9.0 — 治療院ID and 治療院名.
 *
 * 治療院ID still shows the code when RAH holds no clinic for it: that code is what someone types into the
 * 審査 side to find out whose clinic it is, and it is the most useful thing on a clinic_not_found row. Shown
 * PLAIN — no strike-through, no 「(RAH未登録)」 tag, the row's normal colour — so it reads as the value it is
 * rather than as a warning badge.
 *
 * 治療院名 has no such fallback: RAH does not know the clinic, so it does not know a name. 「-」 there.
 */
const ClinicCell = ({
  record,
  field,
}: {
  record: SyncNotification;
  field: 'clinic_number' | 'clinic_name';
}) => {
  const value = record[field] ?? (field === 'clinic_number' ? hospCodeOf(record) : null);

  return <span className="whitespace-nowrap">{value || '-'}</span>;
};

/**
 * BD 15.0 — 追跡ID. The first 8 characters of the UUID on screen, the whole thing on hover and in the
 * clipboard: this is the value someone pastes into the Shinsa side to line up the two halves of one incident,
 * so a truncated copy would be worse than none. Rendered in a monospace face because it is read character by
 * character when comparing two of them.
 */
const CorrelationId = ({ value }: { value: string | null }) => {
  // Same plain 「-」 as the clinic columns — one empty marker, one colour, so a reader scanning the row does
  // not read a shade difference as a difference in meaning.
  if (!value) return <span>-</span>;

  return (
    <AppTooltip title={value}>
      <span
        className="cursor-pointer font-mono text-xs"
        onClick={() => navigator.clipboard?.writeText(value)}
      >
        {value.slice(0, 8)}
      </span>
    </AppTooltip>
  );
};

/**
 * Text held to TWO lines, with the remainder in a tooltip. AppTable's admin mode gives every cell a single
 * nowrap line; these two columns carry the sentences someone actually has to read, and one line of a 500 body
 * is 「HTTP 500: {"success":false,"error":{"code…」 — all envelope, no cause.
 */
const ClampedCell = ({ text, className }: { text: string; className: string }) => (
  <AppTooltip title={text} showOnlyOnOverflow>
    <span className={`line-clamp-2 whitespace-normal ${className}`}>{text}</span>
  </AppTooltip>
);

const SyncNotificationTable = ({
  data,
  onRetry,
  onMarkRead,
  onDownloadCsv,
  downloadingId,
  disabled,
  retryingAggregateId,
  readingId,
  isLoading,
  hasFilters,
  showSiteColumn,
}: SyncNotificationTableProps) => {
  const columns: AppTableColumn<SyncNotification>[] = [
    {
      title: '発生日時',
      dataIndex: 'created_at',
      key: 'created_at',
      // BD 7.0 — 発生日時 as `元号00年00月00日 HH:mm`, the same formatter the clinic mailbox's 受信日 uses.
      render: (value: string) => <span className="whitespace-nowrap">{convGtJDateTime(value)}</span>,
      // Hug idiom (see 操作): 「2026/07/30 22:57」 is a fixed-width fact, so it takes exactly its own width
      // and leaves the rest to エラー内容 instead of holding a share of the table.
      width: 1,
    },
    {
      // BD 8.0 — 治療院ID (clinics.clinic_number, NOT the internal id).
      title: '治療院ID',
      key: 'clinic_number',
      render: (_, record) => <ClinicCell record={record} field="clinic_number" />,
      width: 1,
    },
    {
      // BD 9.0 — 治療院名.
      title: '治療院名',
      key: 'clinic_name',
      render: (_, record) => <ClinicCell record={record} field="clinic_name" />,
      width: 1,
    },
    // BD 10.0 — サイト区分, only where more than one site is in view. On a single-site portal the column
    // would repeat one value down the page; the server decides, since it knows the admin's tenant family.
    ...(showSiteColumn
      ? [
          {
            title: 'サイト区分',
            dataIndex: 'site_type',
            key: 'site_type',
            // 「-」 for a notice that belongs to no site, not 「共通」: the row is still shown on every site
            // (see the backend's scopeToSites), but that is a visibility rule, not a site named 共通 — and a
            // word in this column reads as a value the data holds.
            render: (value: string | null) => (
              <span className="whitespace-nowrap">{value || '-'}</span>
            ),
            width: 1,
            align: 'center' as const,
          },
        ]
      : []),
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      // Two lines, not one: a title is 「同期失敗: rezept-a2 87038」 — cut at one line it loses the id, the
      // only part that says WHICH record. Anything past two lines goes to the tooltip.
      // THIS IS NOW THE ONLY PLACE THE RECORD IS NAMED. The 対象 column (aggregate + aggregate_id) was
      // dropped because BD §3 does not list it, and BD 11.0 puts that duty here — 「Nội dung bao gồm loại lỗi
      // và thông tin xác định bản ghi cần xử lý」. So the clamp above is load-bearing, not cosmetic.
      // The floor lives on the CONTENT, not on the column: next to a 100%-wide neighbour any column width is
      // squeezed down to min-content (a px width gave 73px and cut the id off), while a min-w on the span
      // raises min-content itself, which auto layout has to honour.
      render: (value: string) => <ClampedCell text={value} className="min-w-[184px] break-words" />,
      width: 1,
    },
    {
      title: 'エラー内容',
      dataIndex: 'body',
      key: 'body',
      // break-words, the same as the title. This was break-all while the body was a raw JSON blob — one
      // unbroken string with no spaces to wrap at, where word-wrapping left the line almost empty. BD 12.0
      // (2026-08-14) made the body a Japanese sentence, and break-all on prose splits a word mid-character
      // run at the line end for no gain. The one thing still unbreakable in there — an id like R2-87038 —
      // is short enough to move to the next line whole.
      //
      // min-w on the CONTENT, the same idiom as タイトル above and for the same reason: a px width on the
      // column is only a hint that auto layout drops to min-content the moment the row is crowded, while a
      // min-w on the span RAISES min-content, which auto layout has to honour. Ten columns now sit next to
      // this one, and without a floor 「HTTP 500: {"success":false,…」 was down to the envelope with the cause
      // clipped off — the two lines here are the whole point of the column.
      //
      // Safe to be generous now that the table scrolls (scroll x: true): a floor the viewport cannot meet
      // moves the overflow into a scrollbar instead of squeezing this column to nothing.
      render: (value: string | null) => (
        <ClampedCell text={value ?? ''} className="min-w-[420px] break-words" />
      ),
      // 100% ON TOP OF the floor, not instead of it: the floor is the narrow case, this is the wide one —
      // under auto layout it swallows whatever the sized columns leave over, which keeps that slack out of
      // 操作 and gives it to the column that most deserves the room.
      width: '100%',
    },
    {
      title: '試行回数',
      dataIndex: 'attempts',
      key: 'attempts',
      // A single digit under a 4-character header — the header is the width, and the number is centred so it
      // does not sit alone against the left edge of a column it never fills.
      width: 1,
      align: 'center',
    },
    {
      title: '同期状態',
      key: 'sync_state',
      render: (_, record) => {
        const state = syncStateOf(record);
        return <Tag color={SYNC_STATE_COLORS[state]}>{SYNC_STATE_LABELS[state]}</Tag>;
      },
      width: 1,
      align: 'center',
    },
    {
      title: '既読',
      dataIndex: 'status',
      key: 'read',
      render: (value: string) =>
        value === 'unread' ? (
          <span className="font-bold text-red-600">未読</span>
        ) : (
          <span className="text-gray-400">既読</span>
        ),
      width: 1,
      align: 'center',
    },
    {
      // BD 15.0 — the id that ties this incident to its counterpart on the Shinsa side. Sits after 既読
      // because BD §3 orders it there, between the read state and the buttons.
      title: '追跡ID',
      dataIndex: 'correlation_id',
      key: 'correlation_id',
      render: (value: string | null) => <CorrelationId value={value} />,
      width: 1,
      align: 'center',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        // BD 13.0 「再同期 chỉ hiện ở dòng đang 未同期」 + BD 16.0 「và thuộc trường hợp có thể đồng bộ lại」 —
        // BOTH, which is why this is an && and not just the state.
        //
        // action_type comes from whoever wrote the notice: sync_failed rows carry 'resync', while a
        // reconcile_drift row carries null on purpose — re-pushing cannot fix a frozen version (Shinsa answers
        // 「skipped」 forever) nor bring back a 返戻 message that was lost. Driven by the row's own data rather
        // than by its category, so any future notice type gets this for free.
        const open = syncStateOf(record) === 'pending' && record.action_type === 'resync';
        // shrink-0 on the buttons is what makes the column hug them: flex items shrink by default, so the
        // cell's min-content collapsed and the 「width: 1」 idiom had nothing to fall back to.
        return (
          <div className="flex gap-2 whitespace-nowrap [&>button]:shrink-0">
            {open && (
              <AppButton
                size="small"
                icon={<Refresh />}
                loading={retryingAggregateId === record.aggregate_id}
                disabled={disabled}
                onClick={() => onRetry(record.aggregate, record.aggregate_id)}
              >
                再同期
              </AppButton>
            )}
            {/* 明細CSV — only where the nightly run actually left a file.
                Driven by the row's own flag, not by its category: a drift row whose file could not be
                written must NOT offer a button that answers 404, and any future notice that ships a file
                gets the button without a second condition here. */}
            {record.has_drift_csv && onDownloadCsv && (
              <AppButton
                size="small"
                loading={downloadingId === record.id}
                disabled={disabled}
                onClick={() => onDownloadCsv(record.id)}
              >
                明細CSV
              </AppButton>
            )}
            {record.status === 'unread' && (
              <AppButton
                size="small"
                icon={<Eye />}
                loading={readingId === record.id}
                disabled={disabled}
                onClick={() => onMarkRead(record.id)}
              >
                既読にする
              </AppButton>
            )}
          </div>
        );
      },
      // width 1px + nowrap under tableLayout="auto" is the 「shrink to fit」 idiom: the browser cannot honour
      // 1px, so it falls back to the column's min-content — the buttons themselves. Leaving the width off
      // instead handed this column all the leftover space, which is the strip of emptiness we wanted gone.
      width: 1,
    },
  ];

  return (
    <AppTable
      columns={columns}
      dataSource={data}
      rowKey={record => record.id}
      // NOT isAdminSite here, deliberately: that flag puts `ellipsis` on every column, and rc-table forces
      // table-layout: fixed the moment any column has it — which is what made 操作 take a share of the table
      // instead of the width of its buttons. The only two columns that needed clipping clamp themselves.
      tableLayout="auto"
      // `x: true` — a scrollbar ONLY when the columns genuinely no longer fit.
      //
      // The value matters, and the other two options are both wrong here (@rc-component/table Table.js:271
      // decides the table's own style from it):
      //   true          → width: auto,  min-width: 100%   ← this one. The table sizes to its content under
      //                                                     tableLayout="auto", so it fills a wide viewport
      //                                                     and only overflows once the nowrap columns hit
      //                                                     min-content. An empty table never scrolls.
      //   <number>      → width: 1440px, min-width: 100%  — a FIXED width, so a narrow viewport shows a
      //                                                     scrollbar even with no rows in the table.
      //   'max-content' → width: max-content              — エラー内容 is a 300-character JSON with no spaces,
      //                                                     so its max-content is that whole string on one
      //                                                     line: several thousand px, always scrolling.
      scroll={{ x: true }}
      loading={isLoading}
      // No `locale` override: AppTable's own empty state is the house one (icon + 該当する検索結果がありません。
      // when something is filtered, データがありません。 when not). This screen used to hard-code the filtered
      // sentence, so an inbox that was simply empty claimed a search had excluded everything.
      hasFilters={hasFilters}
    />
  );
};

export default SyncNotificationTable;
